<template>
    <section
        v-if="currentStep === 3"
        class="step transition-all duration-300 py-2 lg:min-h-[500px] flex flex-col"
    >
        <article class="mb-2 md:mb-5 px-6 pt-6 pb-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-700">
            <header class="h-16 pb-4 flex items-center gap-3">
                <div class="rounded-sm overflow-hidden">
                    <ImageLoader>
                        <template #image>
                            <img
                                :src="`${cdnUrl}/${selectedReceivingCard?.card?.card_image?.path}/_medium/${selectedReceivingCard?.card?.card_image?.file_name}`"
                                class="h-12 rounded-sm"
                            />
                        </template>
                        <template #preloader>
                            <div class="h-12 w-12 rounded-sm bg-slate-100 dark:bg-slate-700 shimmer"></div>
                        </template>
                        <template #error>
                            <Icon icon="credit-card" class="w-8 h-5" />
                        </template>
                    </ImageLoader>
                </div>
                <h2 class="text-slate-800 dark:text-slate-100 text-sm font-medium font-['Inter'] leading-tight">
                    {{ selectedReceivingCard?.card?.title }}
                    {{ selectedReceivingCard?.last_four ? `(${selectedReceivingCard?.last_four})` : '' }}
                </h2>
            </header>
            <h3 class="flex items-start justify-start text-slate-600 dark:text-slate-300 text-sm font-medium">
                Details to copy or override
            </h3>
            <div class="selected-details bg-gray-50 dark:bg-transparent pt-4 rounded-lg">
                <ul class="flex flex-col">
                    <li
                        v-for="(detail, i) in copyableDetails"
                        :key="detail.key"
                        class="min-h-11 py-3 border-slate-200 dark:border-slate-700"
                        :class="{ 'border-b': i < copyableDetails.length - 1 }"
                    >
                        <div class="flex flex-col w-full">
                            <div
                                class="flex justify-between items-center"
                                :class="{ 'opacity-50': !isSelected(detail.key) }"
                            >
                                <span class="text-slate-500 dark:text-slate-400 text-sm font-normal font-['Inter'] leading-tight">
                                    {{ detail.label }}
                                </span>
                                <div class="justify-start items-center gap-1 flex">
                                    <div class="text-slate-800 dark:text-slate-100 text-sm font-normal font-['Inter'] leading-tight flex gap-1">
                                        <span
                                            v-if="detail.key === 'annual_fee' && !selectedReceivingCard.user_managed_annual_fee"
                                            :class="{ 'line-through': isSelected(detail.key) }"
                                        >
                                            {{ formatCardDetails(selectedReceivingCard.card.annual_fee, detail.key) }}
                                        </span>
                                        <span v-else :class="{ 'line-through': isSelected(detail.key) }">
                                            <p v-if="detail.key !== 'welcome_bonus' && detail.key !== 'bonus_offers'">
                                                {{ formatCardDetails(selectedReceivingCard[detail.key], detail.key) }}
                                            </p>
                                            <p v-else>{{ !selectedReceivingCard[detail.key] ? 'N/A' : 'Yes' }}</p>
                                        </span>
                                        <span v-if="isSelected(detail.key)" class="flex gap-1 transition-all">
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="flex-shrink-0">
                                                <path d="M2.66675 8.00008H13.3334M13.3334 8.00008L9.33341 4.00008M13.3334 8.00008L9.33341 12.0001" stroke="#64748B" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                                            </svg>
                                            <span>
                                                <p v-if="detail.key !== 'welcome_bonus' && detail.key !== 'bonus_offers'">
                                                    {{ formatCardDetails(selectedCard[detail.key], detail.key) }}
                                                </p>
                                                <p v-else>{{ !selectedCard[detail.key] ? 'N/A' : 'Yes' }}</p>
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <!-- Confirmation: Welcome bonus sub-details -->
                            <Transition name="expand">
                                <template v-if="detail.key === 'welcome_bonus' && selectedDetails[detail.key]">
                                    <ul
                                        v-if="selectedCard?.welcome_bonus && selectedCard?.welcome_bonus?.type !== 'tiered'"
                                        class="pb-3 pl-3 -ml-1.5 mt-3 overflow-hidden transition-all"
                                    >
                                        <li
                                            v-for="(subDetail, i) in welcomeBonusDetails"
                                            :key="subDetail.key"
                                            class="relative flex justify-between pt-3 border-l pl-4"
                                            :class="i < welcomeBonusDetails.length - 1 ? 'border-slate-300 dark:border-slate-600' : 'border-transparent'"
                                        >
                                            <SubDetailConnector :index="i" :total="welcomeBonusDetails.length" class="-top-1/2" />
                                            <p class="text-slate-600 dark:text-slate-300 text-sm font-normal">{{ subDetail.label }}</p>
                                            <p class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                                                {{ formatCardDetails(selectedCard.welcome_bonus[subDetail.key], subDetail.key) }}
                                            </p>
                                        </li>
                                    </ul>
                                    <ul
                                        v-else-if="selectedCard?.welcome_bonus?.type === 'tiered'"
                                        class="pb-4 -mt-1 -ml-1.5 pl-3"
                                    >
                                        <li
                                            v-for="(bonusTier, i) in selectedCard.welcome_bonus?.tiers"
                                            :key="bonusTier.id"
                                            class="relative flex justify-between pt-2 border-l pl-4 h-[52px]"
                                            :class="i < selectedCard.welcome_bonus.tiers.length - 1 ? 'border-slate-300 dark:border-slate-600' : 'border-transparent'"
                                        >
                                            <SubDetailConnector :index="i" :total="selectedCard.welcome_bonus.tiers.length" />
                                            <div class="flex flex-col gap-[2px]">
                                                <span class="text-slate-600 dark:text-slate-300 text-sm font-normal">{{ `Tier ${i + 1}` }}</span>
                                                <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">{{ formatBonusOfferDisplay(bonusTier) }}</span>
                                            </div>
                                            <BonusReceivedBadge :item="bonusTier" />
                                        </li>
                                    </ul>
                                </template>
                            </Transition>
                            <!-- Confirmation: Product changed sub-details -->
                            <Transition name="expand">
                                <ul
                                    v-if="detail.key === 'product_changed' && selectedDetails[detail.key] && selectedCard?.product_changed"
                                    class="pb-3 pl-3 -ml-1.5 mt-3 overflow-hidden transition-all"
                                >
                                    <li
                                        v-for="(subDetail, i) in productChangedDetails"
                                        :key="subDetail.key"
                                        class="relative flex justify-between pt-3 border-l pl-4"
                                        :class="i < productChangedDetails.length - 1 ? 'border-slate-300 dark:border-slate-600' : 'border-transparent'"
                                    >
                                        <SubDetailConnector :index="i" :total="productChangedDetails.length" class="-top-1/2" />
                                        <p class="text-slate-600 dark:text-slate-300 text-sm font-normal">{{ subDetail.label }}</p>
                                        <p class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                                            {{ subDetail.key === 'original_card' ? selectedCard?.original_card?.title : formatCardDetails(selectedCard[subDetail.key], subDetail.key) }}
                                        </p>
                                    </li>
                                </ul>
                            </Transition>
                        </div>
                    </li>
                </ul>
            </div>
        </article>
        <label class="flex items-center ml-1">
            <input
                type="checkbox"
                class="form-checkbox"
                :checked="confirmChanges"
                @change="$emit('update:confirmChanges', $event.target.checked)"
            />
            <span class="ml-2 text-sm">I understand these changes cannot be undone</span>
        </label>
        <Errors :errors="errors['confirmChanges']" class="pt-2 pl-0" />
    </section>
</template>

<script setup>
import { computed } from 'vue';
import Errors from '@/components/errors.vue';
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import SubDetailConnector from './SubDetailConnector.vue';
import BonusReceivedBadge from './BonusReceivedBadge.vue';

defineProps({
    currentStep: { type: Number, required: true },
    selectedCard: { type: Object, default: null },
    selectedReceivingCard: { type: Object, default: null },
    selectedDetails: { type: Object, required: true },
    confirmChanges: { type: Boolean, default: false },
    errors: { type: Object, required: true },
    copyableDetails: { type: Array, required: true },
    welcomeBonusDetails: { type: Array, required: true },
    productChangedDetails: { type: Array, required: true },
    isSelected: { type: Function, required: true },
    formatCardDetails: { type: Function, required: true },
    formatBonusOfferDisplay: { type: Function, required: true },
});

defineEmits(['update:confirmChanges']);

const cdnUrl = computed(() => window.cdnUrl);
</script>
